import { RawAxiosRequestHeaders } from 'axios'

import {
  FacebookEventType,
  GoogleAdsEventType,
  InternalEventType,
  TikTokEventType,
  TwitterEventType,
} from '@/enums/events'
import { PaymentMethod, PaymentPlatform } from '@/enums/payments'
import { uuidv4 } from '@/utils/uuid'

import { EventsApiService } from '@/api'
import {
  TFacebookEvent,
  TFacebookEventData,
  TGoogleAdsEvent,
  TGoogleAdsEventData,
  TInternalAnalyticsEvent,
  TInternalAnalyticsEventData,
  TTikTokEvent,
  TTikTokEventData,
  TTwitterEvent,
  TTwitterEventData,
} from '@/api/types'
import { IUser } from '@/auth/types'
import { confirmEmailCampaign } from '@/core/commonLogic/useEmailCampaignConfirmation'
import { ISession, ISessionContext } from '@/core/session/types'
import { getParamsFromQuery } from '@/core/session/urlManagement'

import { FBPixel } from './fb-pixel'
import { extractAdParams } from './init'

function sendFbqEvent(
  eventId: string,
  sessionId: string,
  eventType: FacebookEventType,
  pixelId: string
) {
  FBPixel.init(pixelId, sessionId)

  const supportedEvents = [
    FacebookEventType.LEAD,
    FacebookEventType.ADD_TO_CART,
    FacebookEventType.INITIATE_CHECKOUT,
    FacebookEventType.ADDED_PAYMENT_INFO,
    FacebookEventType.ADD_TO_WISH_LIST,
  ]

  if (supportedEvents.includes(eventType)) {
    return FBPixel.track(eventType, undefined, { eventID: eventId })
  }
}

const TwitterEventTypeToIdMap: Record<TwitterEventType, string> = {
  [TwitterEventType.LEAD]: 'event_id_lead',
  [TwitterEventType.ADD_TO_WISH_LIST]: 'event_id_add_to_wishlist',
  [TwitterEventType.ADD_TO_CART]: 'event_id_add_to_cart',
  [TwitterEventType.ADDED_PAYMENT_INFO]: 'event_id_add_payment_info',
  [TwitterEventType.INITIATE_CHECKOUT]: 'event_id_initiate_checkout',
}

const trackTwitterEvent = async (
  eventType: TwitterEventType,
  eventData: TTwitterEventData,
  twPixelId: string | undefined,
  sessionContext: ISession,
  user?: IUser | null
) => {
  const twConfig: ITwitterConf | undefined = window.config?.TWITTER_CONF

  if (
    !sessionContext.isAnalyticsEnabled ||
    !twPixelId ||
    !twConfig ||
    (sessionContext.utmParams.utm_source !== 'tw' && !sessionContext.twParams?.twclid)
  ) {
    return
  }
  console.log('trackTwitterEvent', eventType, eventData)

  const headers: RawAxiosRequestHeaders = {
    'Content-Type': 'application/json',
  }

  const eventIdKey = TwitterEventTypeToIdMap[eventType]
  const eventId = twConfig[eventIdKey as keyof ITwitterConf]

  const data: TTwitterEvent = {
    ...eventData,
    event_id: eventId,
    pixel_id: twPixelId,
    conversion_id: uuidv4(),
    twclid: sessionContext.twParams?.twclid,
  }

  if (user) {
    headers['X-BGV-TOKEN'] = user.token
  }

  try {
    await EventsApiService.trackEvents(
      [
        {
          type: 'twitter',
          data,
        },
      ],
      headers
    )
    console.log(`${eventId} event sent successfully`)
  } catch (error) {
    console.log(`Error while sending ${eventId} event`, error)
  }
}

const trackFacebookEvent = async (
  eventType: FacebookEventType,
  eventData: TFacebookEventData,
  pixelId: string,
  sessionContext: ISession,
  user?: IUser | null
) => {
  if (
    !sessionContext.isAnalyticsEnabled ||
    (sessionContext.utmParams.utm_source !== 'fb' && !sessionContext.fbParams?.fbc)
  ) {
    return
  }
  console.log('trackFacebookEvent', eventType, eventData)

  const sessionId = sessionContext.sessionId

  let currentUrl = ''
  try {
    currentUrl = window.location.origin + window.location.pathname + window.location.hash
  } catch {}

  const headers: RawAxiosRequestHeaders = {
    'Content-Type': 'application/json',
  }

  const eventId = uuidv4()
  const data: TFacebookEvent = {
    ...eventData,
    external_id: sessionId,
    event_id: eventId,
    event_name: eventType,
    pixel_id: +pixelId,
    event_source_url: currentUrl,
  }

  const fbUserData = sessionContext.fbParams
  if (fbUserData && fbUserData.fbc) {
    data.fbc = fbUserData.fbc
    data.fbp = fbUserData.fbp
  }

  if (user) {
    headers['X-BGV-TOKEN'] = user.token
  }

  try {
    sendFbqEvent(eventId, sessionId, eventType, pixelId)
  } catch (e) {
    console.log(e)
  }

  try {
    await EventsApiService.trackEvents(
      [
        {
          type: 'facebook',
          data,
        },
      ],
      headers
    )
    console.log(`${eventType} event sent successfully`)
  } catch (error) {
    console.log(`Error while sending ${eventType} event`, error)
  }
}

const trackInternalAnalyticsEvent = async (
  eventType: string,
  eventData: TInternalAnalyticsEventData,
  stageId: string,
  partnerId: string,
  appId: string,
  pageSetId: string,
  sessionContext: ISession | ISessionContext,
  user?: IUser | null
) => {
  if (!sessionContext.isAnalyticsEnabled) {
    return
  }

  const adData = extractAdParams(sessionContext)
  if (adData) {
    eventData = {
      ...eventData,
      ...adData,
    }
  }

  if (eventType != InternalEventType.PAGE_VIEW && 'setEmailCampaign' in sessionContext) {
    confirmEmailCampaign(sessionContext)
  }

  console.log('trackInternalAnalytics', eventType, stageId, eventData)

  const sessionId = sessionContext.sessionId

  const firstVisitAt = sessionContext.fva
  let millisecondsSinceFirstVisit: number = 0
  if (firstVisitAt) {
    millisecondsSinceFirstVisit = +new Date() - +new Date(firstVisitAt)
  }

  const currentUrl = window.location.origin + window.location.pathname + window.location.hash

  const headers: RawAxiosRequestHeaders = {
    'Content-Type': 'application/json',
  }

  const eventId = uuidv4()

  if (user) {
    headers['X-BGV-TOKEN'] = user.token
  }

  const data: TInternalAnalyticsEvent = {
    ...eventData,
    event_id: eventId,
    event: eventType,
    stage_name: stageId,
    partner_id: partnerId,
    app_id: appId,
    page_set_id: pageSetId,
    session_id: sessionId,
    url: currentUrl,
    referer_url: document.referrer || undefined,
    milliseconds_since_first_visit: millisecondsSinceFirstVisit,
  }

  try {
    await EventsApiService.trackEvents(
      [
        {
          type: 'analytics',
          data,
        },
      ],
      headers
    )
    console.log(`${eventType} event sent successfully`)
  } catch (error) {
    console.log(`Error while sending ${eventType} event`, error)
  }
}

const trackTWLead = (
  sessionContext: ISession,
  twPixelId: string | undefined,
  user?: IUser | null
) => {
  return trackTwitterEvent(TwitterEventType.LEAD, {}, twPixelId, sessionContext, user)
}
const trackTWAddToWishList = (
  sessionContext: ISession,
  twPixelId: string | undefined,
  user?: IUser | null
) => {
  return trackTwitterEvent(TwitterEventType.ADD_TO_WISH_LIST, {}, twPixelId, sessionContext, user)
}

const trackTWAddToCart = (
  sessionContext: ISession,
  twPixelId: string | undefined,
  user?: IUser | null
) => {
  return trackTwitterEvent(TwitterEventType.ADD_TO_CART, {}, twPixelId, sessionContext, user)
}

const trackTWAddPaymentInfo = (
  sessionContext: ISession,
  twPixelId: string | undefined,
  user?: IUser | null
) => {
  return trackTwitterEvent(TwitterEventType.ADDED_PAYMENT_INFO, {}, twPixelId, sessionContext, user)
}

const trackTWInitiateCheckout = (
  sessionContext: ISession,
  twPixelId: string | undefined,
  user?: IUser | null
) => {
  return trackTwitterEvent(TwitterEventType.INITIATE_CHECKOUT, {}, twPixelId, sessionContext, user)
}

// Facebook events start
const trackFBLead = (sessionContext: ISession, fbPixelId: string, user?: IUser | null) => {
  return trackFacebookEvent(FacebookEventType.LEAD, {}, fbPixelId, sessionContext, user)
}

const trackFBAddToWishList = (sessionContext: ISession, fbPixelId: string, user?: IUser | null) => {
  return trackFacebookEvent(FacebookEventType.ADD_TO_WISH_LIST, {}, fbPixelId, sessionContext, user)
}

const trackFBAddToCart = (sessionContext: ISession, fbPixelId: string, user?: IUser | null) => {
  return trackFacebookEvent(FacebookEventType.ADD_TO_CART, {}, fbPixelId, sessionContext, user)
}

const trackFBAddPaymentInfo = (
  sessionContext: ISession,
  fbPixelId: string,
  user?: IUser | null
) => {
  return trackFacebookEvent(
    FacebookEventType.ADDED_PAYMENT_INFO,
    {},
    fbPixelId,
    sessionContext,
    user
  )
}

const trackFBInitiateCheckout = (
  sessionContext: ISession,
  fbPixelId: string,
  user?: IUser | null
) => {
  return trackFacebookEvent(
    FacebookEventType.INITIATE_CHECKOUT,
    {},
    fbPixelId,
    sessionContext,
    user
  )
}

// Facebook events end

// track TikTok events

const trackTikTokEvent = async (
  eventType: TikTokEventType,
  eventData: TTikTokEventData,
  tiktokPixelId: string | undefined,
  sessionContext: ISession,
  user?: IUser | null
) => {
  if (
    !sessionContext.isAnalyticsEnabled ||
    (sessionContext.utmParams.utm_source !== 'tt' && !sessionContext.tikTokParams?.ttclid) ||
    !tiktokPixelId
  ) {
    return
  }
  console.log('trackTikTokEvent', eventType, eventData)

  const sessionId = sessionContext.sessionId

  let currentUrl = ''
  try {
    currentUrl = window.location.origin + window.location.pathname + window.location.hash
  } catch {}

  const headers: RawAxiosRequestHeaders = {
    'Content-Type': 'application/json',
  }

  const eventId = uuidv4()
  const data: TTikTokEvent = {
    ...eventData,
    external_id: sessionId,
    event_id: eventId,
    event_name: eventType,
    pixel_id: tiktokPixelId,
    event_source_url: currentUrl,
  }

  const tikTokUserData = sessionContext.tikTokParams
  if (tikTokUserData && tikTokUserData.ttclid) {
    data.ttclid = tikTokUserData.ttclid
  }

  const urlParams = getParamsFromQuery()
  if (urlParams.test_event_code) {
    console.log('test event code:', urlParams.test_event_code)
    data.test_event_code = urlParams.test_event_code
  }

  if (user) {
    headers['X-BGV-TOKEN'] = user.token
  }

  try {
    await EventsApiService.trackEvents(
      [
        {
          type: 'tiktok',
          data,
        },
      ],
      headers
    )
    console.log(`${eventType} event sent successfully`)
  } catch (error) {
    console.log(`Error while sending ${eventType} event`, error)
  }
}

const trackTikTokLead = (
  sessionContext: ISession,
  tiktokPixelId: string | undefined,
  user?: IUser | null
) => {
  return trackTikTokEvent(TikTokEventType.LEAD, {}, tiktokPixelId, sessionContext, user)
}

const trackTikTokAddToWishList = (
  sessionContext: ISession,
  tiktokPixelId: string | undefined,
  user?: IUser | null
) => {
  return trackTikTokEvent(TikTokEventType.ADD_TO_WISH_LIST, {}, tiktokPixelId, sessionContext, user)
}

const trackTikTokAddToCart = (
  sessionContext: ISession,
  tiktokPixelId: string | undefined,
  user?: IUser | null
) => {
  return trackTikTokEvent(TikTokEventType.ADD_TO_CART, {}, tiktokPixelId, sessionContext, user)
}

const trackTikTokAddPaymentInfo = (
  sessionContext: ISession,
  tiktokPixelId: string | undefined,
  user?: IUser | null
) => {
  return trackTikTokEvent(
    TikTokEventType.ADDED_PAYMENT_INFO,
    {},
    tiktokPixelId,
    sessionContext,
    user
  )
}

const trackTikTokInitiateCheckout = (
  sessionContext: ISession,
  tiktokPixelId: string | undefined,
  user?: IUser | null
) => {
  return trackTikTokEvent(
    TikTokEventType.INITIATE_CHECKOUT,
    {},
    tiktokPixelId,
    sessionContext,
    user
  )
}

// TikTok events end

// track GoogleAds events

const trackGoogleAdsEvent = async (
  eventType: GoogleAdsEventType,
  eventData: TGoogleAdsEventData,
  sessionContext: ISession,
  user?: IUser | null
) => {
  const gaConfig: IGoogleAdsConfig | undefined = window.config?.GOOGLE_ADS_CONFIG

  if (
    !sessionContext.isAnalyticsEnabled ||
    !gaConfig ||
    !(
      sessionContext.utmParams.utm_source === 'ga' &&
      (sessionContext.gaParams?.gclid ||
        sessionContext.gaParams?.gbraid ||
        sessionContext.gaParams?.wbraid)
    )
  ) {
    return
  }
  console.log('trackGoogleAdsEvent', eventType, eventData)

  const headers: RawAxiosRequestHeaders = {
    'Content-Type': 'application/json',
  }

  const conversionId = gaConfig.conversions[eventType]

  const { currency, value } = eventData

  const eventId = uuidv4()
  const data: TGoogleAdsEvent = {
    event_id: eventId,
    customer_id: gaConfig.customer_id,
    external_id: sessionContext.sessionId,
    conversion_id: conversionId,
    conversion_value: value,
    currency,
    gclid: sessionContext.gaParams?.gclid,
    gbraid: sessionContext.gaParams?.gbraid,
    wbraid: sessionContext.gaParams?.wbraid,
  }

  if (user) {
    headers['X-BGV-TOKEN'] = user.token
  }

  try {
    await EventsApiService.trackEvents(
      [
        {
          type: 'google_ads',
          data,
        },
      ],
      headers
    )
    console.log(`${eventId} event sent successfully`)
  } catch (error) {
    console.log(`Error while sending ${eventId} event`, error)
  }
}

const trackGoogleAdsLead = (sessionContext: ISession, user?: IUser | null) => {
  return trackGoogleAdsEvent(GoogleAdsEventType.LEAD, {}, sessionContext, user)
}

const trackGoogleAdsAddToWishList = (sessionContext: ISession, user?: IUser | null) => {
  return trackGoogleAdsEvent(GoogleAdsEventType.ADD_TO_WISH_LIST, {}, sessionContext, user)
}

const trackGoogleAdsAddToCart = (sessionContext: ISession, user?: IUser | null) => {
  return trackGoogleAdsEvent(GoogleAdsEventType.ADD_TO_CART, {}, sessionContext, user)
}

const trackGoogleAdsAddPaymentInfo = (sessionContext: ISession, user?: IUser | null) => {
  return trackGoogleAdsEvent(GoogleAdsEventType.ADDED_PAYMENT_INFO, {}, sessionContext, user)
}

const trackGoogleAdsInitiateCheckout = (sessionContext: ISession, user?: IUser | null) => {
  return trackGoogleAdsEvent(GoogleAdsEventType.INITIATE_CHECKOUT, {}, sessionContext, user)
}

// GoogleAds events end

//Internal events start

export const trackInternalSessionStart = (
  partnerId: string,
  appId: string,
  pageSetId: string,
  sessionContext: ISession,
  user?: IUser | null,
  stageValues?: string[],
  stageVariations?: string[]
) => {
  return trackInternalAnalyticsEvent(
    InternalEventType.SESSION_START,
    {
      stage_values: stageValues || [],
      stage_variation: stageVariations || [],
    },
    'landed',
    partnerId,
    appId,
    pageSetId,
    sessionContext,
    user
  )
}

export const trackPageView = (
  stageId: string,
  partnerId: string,
  appId: string,
  pageSetId: string,
  sessionContext: ISession,
  user?: IUser | null,
  stageValues?: string[],
  stageVariations?: string[],
  planId?: string,
  paymentMethod?: PaymentMethod,
  paymentPlatform?: PaymentPlatform,
  ltpContentPriceId?: string
) => {
  return trackInternalAnalyticsEvent(
    InternalEventType.PAGE_VIEW,
    {
      stage_values: stageValues || [],
      stage_variation: stageVariations || [],
      plan_id: planId,
      ltp_content_price_id: ltpContentPriceId,
      payment_method: paymentMethod,
      payment_platform: paymentPlatform,
    },
    stageId,
    partnerId,
    appId,
    pageSetId,
    sessionContext,
    user
  )
}

export const trackPageComplete = (
  stageId: string,
  stageValues: string[],
  partnerId: string,
  appId: string,
  pageSetId: string,
  sessionContext: ISession,
  user?: IUser | null,
  stageVariations?: string[],
  planId?: string,
  paymentMethod?: PaymentMethod,
  paymentPlatform?: PaymentPlatform,
  contentPriceId?: string
) => {
  return trackInternalAnalyticsEvent(
    InternalEventType.PAGE_COMPLETE,
    {
      stage_variation: stageVariations || [],
      stage_values: stageValues,
      plan_id: planId,
      payment_method: paymentMethod,
      payment_platform: paymentPlatform,
      ltp_content_price_id: contentPriceId,
    },
    stageId,
    partnerId,
    appId,
    pageSetId,
    sessionContext,
    user
  )
}

const trackInternalRegistrationSuccess = (
  stageId: string,
  partnerId: string,
  appId: string,
  pageSetId: string,
  sessionContext: ISession,
  user?: IUser | null,
  stageValues?: string[],
  stageVariations?: string[]
) => {
  return trackInternalAnalyticsEvent(
    InternalEventType.REGISTRATION_SUCCESS,
    { stage_variation: stageVariations || [], stage_values: stageValues || [] },
    stageId,
    partnerId,
    appId,
    pageSetId,
    sessionContext,
    user
  )
}

const trackInternalLogin = (
  stageId: string,
  partnerId: string,
  appId: string,
  pageSetId: string,
  sessionContext: ISession,
  user?: IUser | null,
  stageValues?: string[],
  stageVariations?: string[]
) => {
  return trackInternalAnalyticsEvent(
    InternalEventType.LOGIN,
    { stage_variation: stageVariations || [], stage_values: stageValues || [] },
    stageId,
    partnerId,
    appId,
    pageSetId,
    sessionContext,
    user
  )
}

export const trackRegistrationFailed = (
  stageId: string,
  partnerId: string,
  appId: string,
  pageSetId: string,
  sessionContext: ISession,
  user?: IUser | null,
  stageValues?: string[],
  stageVariations?: string[]
) => {
  return trackInternalAnalyticsEvent(
    InternalEventType.REGISTRATION_FAILED,
    { stage_variation: stageVariations || [], stage_values: stageValues || [] },
    stageId,
    partnerId,
    appId,
    pageSetId,
    sessionContext,
    user
  )
}

export const trackVerificationSuccess = (
  stageId: string,
  partnerId: string,
  appId: string,
  pageSetId: string,
  sessionContext: ISession,
  user?: IUser | null,
  stageValues?: string[],
  stageVariations?: string[]
) => {
  return trackInternalAnalyticsEvent(
    InternalEventType.VERIFICATION_SUCCESS,
    { stage_variation: stageVariations || [], stage_values: stageValues || [] },
    stageId,
    partnerId,
    appId,
    pageSetId,
    sessionContext,
    user
  )
}

export const trackVerificationFailed = (
  stageId: string,
  partnerId: string,
  appId: string,
  pageSetId: string,
  sessionContext: ISession,
  user?: IUser | null,
  stageValues?: string[],
  stageVariations?: string[]
) => {
  return trackInternalAnalyticsEvent(
    InternalEventType.VERIFICATION_FAILED,
    { stage_variation: stageVariations || [], stage_values: stageValues || [] },
    stageId,
    partnerId,
    appId,
    pageSetId,
    sessionContext,
    user
  )
}

export const trackInternalPlanClick = (
  stageId: string,
  partnerId: string,
  appId: string,
  pageSetId: string,
  planId: string,
  sessionContext: ISession,
  user?: IUser | null,
  stageValues?: string[],
  stageVariations?: string[]
) => {
  return trackInternalAnalyticsEvent(
    InternalEventType.PLAN_CLICK,
    { stage_variation: stageVariations || [], stage_values: stageValues || [], plan_id: planId },
    stageId,
    partnerId,
    appId,
    pageSetId,
    sessionContext,
    user
  )
}

const trackInternalPlanSelected = (
  stageId: string,
  stageValues: string[],
  partnerId: string,
  appId: string,
  pageSetId: string,
  planId: string,
  sessionContext: ISession,
  user?: IUser | null,
  stageVariations?: string[]
) => {
  return trackInternalAnalyticsEvent(
    InternalEventType.PLAN_SELECTED,
    { stage_variation: stageVariations || [], stage_values: stageValues, plan_id: planId },
    stageId,
    partnerId,
    appId,
    pageSetId,
    sessionContext,
    user
  )
}

const trackInternalPaymentInfoAdded = (
  stageId: string,
  partnerId: string,
  appId: string,
  pageSetId: string,
  planId: string,
  paymentMethod: PaymentMethod,
  paymentPlatform: PaymentPlatform,
  sessionContext: ISession,
  user?: IUser | null,
  stageValues?: string[],
  stageVariations?: string[]
) => {
  return trackInternalAnalyticsEvent(
    InternalEventType.PAYMENT_INFO_ADDED,
    {
      stage_variation: stageVariations || [],
      stage_values: stageValues || [],
      plan_id: planId,
      payment_method: paymentMethod,
      payment_platform: paymentPlatform,
    },
    stageId,
    partnerId,
    appId,
    pageSetId,
    sessionContext,
    user
  )
}

const trackInternalPaymentClick = (
  stageId: string,
  partnerId: string,
  appId: string,
  pageSetId: string,
  planId: string,
  paymentMethod: PaymentMethod,
  paymentPlatform: PaymentPlatform,
  sessionContext: ISession,
  user?: IUser | null,
  stageValues?: string[],
  stageVariations?: string[]
) => {
  return trackInternalAnalyticsEvent(
    InternalEventType.PAYMENT_CLICK,
    {
      stage_variation: stageVariations || [],
      stage_values: stageValues || [],
      plan_id: planId,
      payment_method: paymentMethod,
      payment_platform: paymentPlatform,
    },
    stageId,
    partnerId,
    appId,
    pageSetId,
    sessionContext,
    user
  )
}

export const trackPurchaseStarted = (
  stageId: string,
  partnerId: string,
  appId: string,
  pageSetId: string,
  planId: string,
  paymentMethod: PaymentMethod,
  paymentPlatform: PaymentPlatform,
  sessionContext: ISession,
  user?: IUser | null,
  stageValues?: string[],
  stageVariations?: string[]
) => {
  return trackInternalAnalyticsEvent(
    InternalEventType.PURCHASE_STARTED,
    {
      stage_values: stageValues || [],
      stage_variation: stageVariations || [],
      plan_id: planId,
      payment_method: paymentMethod,
      payment_platform: paymentPlatform,
    },
    stageId,
    partnerId,
    appId,
    pageSetId,
    sessionContext,
    user
  )
}

export const trackPaymentScreen = (
  stageId: string,
  partnerId: string,
  appId: string,
  pageSetId: string,
  planId: string,
  sessionContext: ISession,
  user?: IUser | null,
  stageValues?: string[],
  stageVariations?: string[]
) => {
  return trackInternalAnalyticsEvent(
    InternalEventType.PAYMENT_SCREEN,
    {
      stage_values: stageValues || [],
      stage_variation: stageVariations || [],
      plan_id: planId,
    },
    stageId,
    partnerId,
    appId,
    pageSetId,
    sessionContext,
    user
  )
}

// LTP internal
export const trackInternalLTPPriceClick = (
  stageId: string,
  partnerId: string,
  appId: string,
  pageSetId: string,
  contentPriceId: string,
  sessionContext: ISession,
  user?: IUser | null,
  stageValues?: string[],
  stageVariations?: string[]
) => {
  return trackInternalAnalyticsEvent(
    InternalEventType.LTP_PRICE_CLICK,
    {
      stage_variation: stageVariations || [],
      stage_values: stageValues || [],
      ltp_content_price_id: contentPriceId,
    },
    stageId,
    partnerId,
    appId,
    pageSetId,
    sessionContext,
    user
  )
}

const trackInternalLTPPriceSelected = (
  stageId: string,
  stageValues: string[],
  partnerId: string,
  appId: string,
  pageSetId: string,
  contentPriceId: string,
  sessionContext: ISession,
  user?: IUser | null,
  stageVariations?: string[]
) => {
  return trackInternalAnalyticsEvent(
    InternalEventType.LTP_PRICE_SELECTED,
    {
      stage_variation: stageVariations || [],
      stage_values: stageValues,
      ltp_content_price_id: contentPriceId,
    },
    stageId,
    partnerId,
    appId,
    pageSetId,
    sessionContext,
    user
  )
}

const trackInternalLTPPaymentInfoAdded = (
  stageId: string,
  partnerId: string,
  appId: string,
  pageSetId: string,
  contentPriceId: string,
  paymentMethod: PaymentMethod,
  paymentPlatform: PaymentPlatform,
  sessionContext: ISession,
  user?: IUser | null,
  stageValues?: string[],
  stageVariations?: string[]
) => {
  return trackInternalAnalyticsEvent(
    InternalEventType.LTP_PAYMENT_INFO_ADDED,
    {
      stage_variation: stageVariations || [],
      stage_values: stageValues || [],
      ltp_content_price_id: contentPriceId,
      payment_method: paymentMethod,
      payment_platform: paymentPlatform,
    },
    stageId,
    partnerId,
    appId,
    pageSetId,
    sessionContext,
    user
  )
}

const trackInternalLTPPaymentClick = (
  stageId: string,
  partnerId: string,
  appId: string,
  pageSetId: string,
  contentPriceId: string,
  paymentMethod: PaymentMethod,
  paymentPlatform: PaymentPlatform,
  sessionContext: ISession,
  user?: IUser | null,
  stageValues?: string[],
  stageVariations?: string[]
) => {
  return trackInternalAnalyticsEvent(
    InternalEventType.LTP_PAYMENT_CLICK,
    {
      stage_variation: stageVariations || [],
      stage_values: stageValues || [],
      ltp_content_price_id: contentPriceId,
      payment_method: paymentMethod,
      payment_platform: paymentPlatform,
    },
    stageId,
    partnerId,
    appId,
    pageSetId,
    sessionContext,
    user
  )
}

export const trackLTPPaymentScreen = (
  stageId: string,
  partnerId: string,
  appId: string,
  pageSetId: string,
  contentPriceId: string,
  sessionContext: ISession,
  user?: IUser | null,
  stageValues?: string[],
  stageVariations?: string[]
) => {
  return trackInternalAnalyticsEvent(
    InternalEventType.LTP_PAYMENT_SCREEN,
    {
      stage_values: stageValues || [],
      stage_variation: stageVariations || [],
      ltp_content_price_id: contentPriceId,
    },
    stageId,
    partnerId,
    appId,
    pageSetId,
    sessionContext,
    user
  )
}

export const trackLTPPurchaseStarted = (
  stageId: string,
  partnerId: string,
  appId: string,
  pageSetId: string,
  contentPriceId: string,
  paymentMethod: PaymentMethod,
  paymentPlatform: PaymentPlatform,
  sessionContext: ISession,
  user?: IUser | null,
  stageValues?: string[],
  stageVariations?: string[]
) => {
  return trackInternalAnalyticsEvent(
    InternalEventType.LTP_PURCHASE_STARTED,
    {
      stage_values: stageValues || [],
      stage_variation: stageVariations || [],
      ltp_content_price_id: contentPriceId,
      payment_method: paymentMethod,
      payment_platform: paymentPlatform,
    },
    stageId,
    partnerId,
    appId,
    pageSetId,
    sessionContext,
    user
  )
}

//Internal events end

export const trackSessionStart = (
  partnerId: string,
  appId: string,
  pageSetId: string,
  sessionContext: ISession,
  stageValues?: string[],
  stageVariations?: string[]
) => {
  return Promise.all([
    trackInternalSessionStart(
      partnerId,
      appId,
      pageSetId,
      sessionContext,
      null,
      stageValues,
      stageVariations
    ),
  ])
}

export const trackRegistrationSuccess = (
  stageId: string,
  partnerId: string,
  appId: string,
  pageSetId: string,
  sessionContext: ISession,
  fbPixelId: string,
  twPixelId: string | undefined,
  tiktokPixelId: string | undefined,
  user?: IUser | null,
  stageValues?: string[],
  stageVariations?: string[]
) => {
  return Promise.all([
    trackFBLead(sessionContext, fbPixelId, user),
    trackTWLead(sessionContext, twPixelId, user),
    trackTikTokLead(sessionContext, tiktokPixelId, user),
    trackGoogleAdsLead(sessionContext, user),
    trackInternalRegistrationSuccess(
      stageId,
      partnerId,
      appId,
      pageSetId,
      sessionContext,
      user,
      stageValues,
      stageVariations
    ),
  ])
}

export const trackLead = (
  sessionContext: ISession,
  fbPixelId: string,
  twPixelId: string | undefined,
  tiktokPixelId: string | undefined,
  user?: IUser | null
) => {
  return Promise.all([
    trackFBLead(sessionContext, fbPixelId, user),
    trackTWLead(sessionContext, twPixelId, user),
    trackTikTokLead(sessionContext, tiktokPixelId, user),
    trackGoogleAdsLead(sessionContext, user),
  ])
}

export const trackLogin = (
  stageId: string,
  partnerId: string,
  appId: string,
  pageSetId: string,
  sessionContext: ISession,
  user?: IUser | null,
  stageValues?: string[],
  stageVariations?: string[]
) => {
  return trackInternalLogin(
    stageId,
    partnerId,
    appId,
    pageSetId,
    sessionContext,
    user,
    stageValues,
    stageVariations
  )
}

export const trackPlanClick = (
  stageId: string,
  partnerId: string,
  appId: string,
  pageSetId: string,
  planId: string,
  sessionContext: ISession,
  fbPixelId: string,
  twPixelId: string | undefined,
  tiktokPixelId: string | undefined,
  user?: IUser | null,
  stageValues?: string[],
  stageVariations?: string[]
) => {
  return Promise.all([
    trackFBAddToWishList(sessionContext, fbPixelId, user),
    trackTWAddToWishList(sessionContext, twPixelId, user),
    trackTikTokAddToWishList(sessionContext, tiktokPixelId, user),
    trackGoogleAdsAddToWishList(sessionContext, user),
    trackInternalPlanClick(
      stageId,
      partnerId,
      appId,
      pageSetId,
      planId,
      sessionContext,
      user,
      stageValues,
      stageVariations
    ),
  ])
}

export const trackPlanSelected = (
  stageId: string,
  stageValues: string[],
  partnerId: string,
  appId: string,
  pageSetId: string,
  planId: string,
  sessionContext: ISession,
  user?: IUser | null,
  stageVariations?: string[]
) => {
  return trackInternalPlanSelected(
    stageId,
    stageValues,
    partnerId,
    appId,
    pageSetId,
    planId,
    sessionContext,
    user,
    stageVariations
  )
}

export const trackAddToCart = (
  sessionContext: ISession,
  fbPixelId: string,
  twPixelId: string | undefined,
  tiktokPixelId: string | undefined,
  user?: IUser | null
) => {
  return Promise.all([
    trackFBAddToCart(sessionContext, fbPixelId, user),
    trackTWAddToCart(sessionContext, twPixelId, user),
    trackTikTokAddToCart(sessionContext, tiktokPixelId, user),
    trackGoogleAdsAddToCart(sessionContext, user),
  ])
}

export const trackPaymentInfoAdded = (
  stageId: string,
  partnerId: string,
  appId: string,
  pageSetId: string,
  planId: string,
  paymentMethod: PaymentMethod,
  paymentPlatform: PaymentPlatform,
  sessionContext: ISession,
  fbPixelId: string,
  twPixelId: string | undefined,
  tiktokPixelId: string | undefined,
  user?: IUser | null,
  stageValues?: string[],
  stageVariations?: string[]
) => {
  return Promise.all([
    trackFBInitiateCheckout(sessionContext, fbPixelId, user),
    trackTWInitiateCheckout(sessionContext, twPixelId, user),
    trackTikTokInitiateCheckout(sessionContext, tiktokPixelId, user),
    trackGoogleAdsInitiateCheckout(sessionContext, user),
    trackInternalPaymentInfoAdded(
      stageId,
      partnerId,
      appId,
      pageSetId,
      planId,
      paymentMethod,
      paymentPlatform,
      sessionContext,
      user,
      stageValues,
      stageVariations
    ),
  ])
}

export const trackPaymentClick = (
  stageId: string,
  partnerId: string,
  appId: string,
  pageSetId: string,
  planId: string,
  paymentMethod: PaymentMethod,
  paymentPlatform: PaymentPlatform,
  sessionContext: ISession,
  fbPixelId: string,
  twPixelId: string | undefined,
  tiktokPixelId: string | undefined,
  user?: IUser | null,
  stageValues?: string[],
  stageVariations?: string[]
) => {
  return Promise.all([
    trackFBAddPaymentInfo(sessionContext, fbPixelId, user),
    trackTWAddPaymentInfo(sessionContext, twPixelId, user),
    trackTikTokAddPaymentInfo(sessionContext, tiktokPixelId, user),
    trackGoogleAdsAddPaymentInfo(sessionContext, user),
    trackInternalPaymentClick(
      stageId,
      partnerId,
      appId,
      pageSetId,
      planId,
      paymentMethod,
      paymentPlatform,
      sessionContext,
      user,
      stageValues,
      stageVariations
    ),
  ])
}

export const trackAppCredentialsView = (
  stageId: string,
  partnerId: string,
  appId: string,
  pageSetId: string,
  sessionContext: ISession,
  planId?: string,
  paymentMethod?: PaymentMethod | undefined,
  paymentPlatform?: PaymentPlatform | undefined,
  user?: IUser | null,
  stageValues?: string[],
  stageVariations?: string[]
) => {
  return trackInternalAnalyticsEvent(
    InternalEventType.APP_CREDENTIALS_VIEW,
    {
      stage_values: stageValues || [],
      stage_variation: stageVariations || [],
      plan_id: planId,
      payment_method: paymentMethod,
      payment_platform: paymentPlatform,
    },
    stageId,
    partnerId,
    appId,
    pageSetId,
    sessionContext,
    user
  )
}

export const trackLTPPriceClick = (
  stageId: string,
  partnerId: string,
  appId: string,
  pageSetId: string,
  contentPriceId: string,
  sessionContext: ISession,
  user?: IUser | null,
  stageValues?: string[],
  stageVariations?: string[]
) => {
  return Promise.all([
    trackInternalLTPPriceClick(
      stageId,
      partnerId,
      appId,
      pageSetId,
      contentPriceId,
      sessionContext,
      user,
      stageValues,
      stageVariations
    ),
  ])
}

export const trackLTPPriceSelected = (
  stageId: string,
  stageValues: string[],
  partnerId: string,
  appId: string,
  pageSetId: string,
  contentPriceId: string,
  sessionContext: ISession,
  user?: IUser | null,
  stageVariations?: string[]
) => {
  return trackInternalLTPPriceSelected(
    stageId,
    stageValues,
    partnerId,
    appId,
    pageSetId,
    contentPriceId,
    sessionContext,
    user,
    stageVariations
  )
}

export const trackLTPPaymentInfoAdded = (
  stageId: string,
  partnerId: string,
  appId: string,
  pageSetId: string,
  contentPriceId: string,
  paymentMethod: PaymentMethod,
  paymentPlatform: PaymentPlatform,
  sessionContext: ISession,
  user?: IUser | null,
  stageValues?: string[],
  stageVariations?: string[]
) => {
  return Promise.all([
    trackInternalLTPPaymentInfoAdded(
      stageId,
      partnerId,
      appId,
      pageSetId,
      contentPriceId,
      paymentMethod,
      paymentPlatform,
      sessionContext,
      user,
      stageValues,
      stageVariations
    ),
  ])
}

export const trackLTPPaymentClick = (
  stageId: string,
  partnerId: string,
  appId: string,
  pageSetId: string,
  contentPriceId: string,
  paymentMethod: PaymentMethod,
  paymentPlatform: PaymentPlatform,
  sessionContext: ISession,
  user?: IUser | null,
  stageValues?: string[],
  stageVariations?: string[]
) => {
  return Promise.all([
    trackInternalLTPPaymentClick(
      stageId,
      partnerId,
      appId,
      pageSetId,
      contentPriceId,
      paymentMethod,
      paymentPlatform,
      sessionContext,
      user,
      stageValues,
      stageVariations
    ),
  ])
}

export const trackAppLTPDataView = (
  stageId: string,
  partnerId: string,
  appId: string,
  pageSetId: string,
  sessionContext: ISession,
  contentPriceId?: string,
  paymentMethod?: PaymentMethod | undefined,
  paymentPlatform?: PaymentPlatform | undefined,
  user?: IUser | null,
  stageValues?: string[],
  stageVariations?: string[]
) => {
  return trackInternalAnalyticsEvent(
    InternalEventType.LTP_DATA_VIEW,
    {
      stage_values: stageValues || [],
      stage_variation: stageVariations || [],
      ltp_content_price_id: contentPriceId,
      payment_method: paymentMethod,
      payment_platform: paymentPlatform,
    },
    stageId,
    partnerId,
    appId,
    pageSetId,
    sessionContext,
    user
  )
}
